import {
  Activity,
  ArrowLeft,
  CalendarClock,
  CheckCircle2,
  Clock3,
  Copy,
  Download,
  Eye,
  EyeOff,
  Fingerprint,
  Globe2,
  History,
  KeyRound,
  PackageCheck,
  PauseCircle,
  PlayCircle,
  RefreshCw,
  Link2,
  RotateCcw,
  Server,
  ShieldCheck,
  Trash2,
  TriangleAlert,
  UserRoundCog,
} from "lucide-react";
import { useCallback, useEffect, useState, type CSSProperties, type FormEvent } from "react";
import { Link, useParams } from "../router";

import { api, ApiError, jsonBody } from "../api";
import { useAuth } from "../auth";
import { ProductMark } from "../components/license-registry";
import {
  Button,
  Capacity,
  ConfirmDialog,
  CopyButton,
  InlineNotice,
  LoadingState,
  StatusBadge,
  formatDate,
  relativeTime,
} from "../components/ui";
import type { LicenseDetail } from "../types";

type AdminAction =
  | "disable-public"
  | "enable-public"
  | "reactivate"
  | "resend-key"
  | "reset-activations"
  | "resume"
  | "rotate"
  | "suspend"
  | "sync-role"
  | "transfer-owner";

type PendingAction =
  | { kind: "remove"; ip: LicenseDetail["ipAddresses"][number] }
  | { kind: "reset" }
  | null;

export function LicenseDetailPage() {
  const { account } = useAuth();
  const { id = "" } = useParams();
  const [license, setLicense] = useState<LicenseDetail | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [revealedKey, setRevealedKey] = useState<string | null>(null);
  const [pendingAction, setPendingAction] = useState<PendingAction>(null);
  const [override, setOverride] = useState(false);
  const [overrideReason, setOverrideReason] = useState("");

  const load = useCallback(() => {
    setError(null);
    return api<LicenseDetail>(`/v1/web/licenses/${id}`)
      .then(setLicense)
      .catch((reason) => setError(reason instanceof Error ? reason.message : "License details could not be loaded."));
  }, [id]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    if (!revealedKey) return;
    const timer = window.setTimeout(() => setRevealedKey(null), 30_000);
    return () => window.clearTimeout(timer);
  }, [revealedKey]);

  async function revealKey() {
    setBusy(true);
    setError(null);
    try {
      const result = await api<{ licenseKey: string }>(`/v1/web/licenses/${id}/reveal`, { method: "POST" });
      setRevealedKey(result.licenseKey);
    } catch (reason) {
      setError(reason instanceof ApiError ? reason.message : "The key could not be revealed.");
    } finally {
      setBusy(false);
    }
  }

  async function confirmAction() {
    if (!pendingAction) return;
    setBusy(true);
    setError(null);
    try {
      const body = jsonBody({ override, ...(overrideReason.trim() ? { reason: overrideReason.trim() } : {}) });
      if (pendingAction.kind === "remove") {
        const result = await api<{ removedActivationCount: number }>(`/v1/web/licenses/${id}/ips/${pendingAction.ip.id}`, { body, method: "DELETE" });
        setNotice(`${pendingAction.ip.ipLabel} and ${result.removedActivationCount} linked activation${result.removedActivationCount === 1 ? "" : "s"} were removed.`);
      } else {
        const result = await api<{ removedActivationCount: number; removedCount: number }>(`/v1/web/licenses/${id}/ips/reset`, { body, method: "POST" });
        setNotice(`${result.removedCount} IP record${result.removedCount === 1 ? "" : "s"} and ${result.removedActivationCount} activation${result.removedActivationCount === 1 ? "" : "s"} were removed.`);
      }
      setPendingAction(null);
      setOverride(false);
      setOverrideReason("");
      await load();
    } catch (reason) {
      setError(reason instanceof ApiError ? reason.message : "The IP action could not be completed.");
      setPendingAction(null);
    } finally {
      setBusy(false);
    }
  }

  if (!license && !error) return <LoadingState label="Loading license record" />;
  if (!license) return <div className="page"><InlineNotice kind="danger">{error}</InlineNotice></div>;
  const resetAvailable = !license.resetAvailableAt || new Date(license.resetAvailableAt).getTime() <= Date.now();
  const removeAvailable = !license.removeAvailableAt || new Date(license.removeAvailableAt).getTime() <= Date.now();

  return (
    <div className="page license-detail">
      <Link className="back-link" to="/licenses"><ArrowLeft size={16} /> Back to licenses</Link>
      <section
        className={`license-hero ${license.product.bannerUrl ? "license-hero--with-art" : ""}`}
        style={license.product.bannerUrl ? { "--product-banner": `url(${license.product.bannerUrl})` } as CSSProperties : undefined}
      >
        <div className="license-hero__identity">
          <ProductMark product={license.product} />
          <div>
            <span className="page-context">{license.product.slug}</span>
            <div className="license-title"><h1>{license.product.name}</h1><StatusBadge status={license.status} />{license.isPublic && <span className="public-use-badge"><Globe2 size={13} /> Public use</span>}</div>
            <p>{license.product.description || "Premium Minecraft plugin license managed by oCloud Studio."}</p>
          </div>
        </div>
        <dl className="license-facts">
          <div><dt>Access</dt><dd><StatusBadge status={license.accessLevel} /></dd></div>
          <div><dt>Expires</dt><dd>{formatDate(license.expiresAt)}</dd></div>
          <div><dt>Created</dt><dd>{formatDate(license.createdAt)}</dd></div>
        </dl>
      </section>

      {error && <InlineNotice kind="danger">{error}</InlineNotice>}
      {notice && <InlineNotice kind="success">{notice}</InlineNotice>}
      {license.revocationReason && (
        <InlineNotice kind={license.status === "EXPIRED" ? "warning" : "danger"}>
          <strong>{license.status === "EXPIRED" ? "Expiration reason" : "Revocation reason"}:</strong> {license.revocationReason}
        </InlineNotice>
      )}

      <section className="key-vault">
        <div className="key-vault__icon"><KeyRound size={20} /></div>
        <div className="key-vault__copy"><span>License key</span><code>{revealedKey || `${license.keyPrefix}••••••••••••••••`}</code><small>Automatically hidden again after 30 seconds.</small></div>
        <div className="key-vault__actions">
          {revealedKey && <CopyButton label="Copy license key" value={revealedKey} />}
          <Button disabled={busy} onClick={() => revealedKey ? setRevealedKey(null) : void revealKey()} variant="secondary">
            {revealedKey ? <EyeOff size={16} /> : <Eye size={16} />}
            {revealedKey ? "Hide" : "Reveal key"}
          </Button>
        </div>
      </section>

      <div className="detail-grid">
        <div className="detail-main">
          <ReleaseDownloads license={license} />

          <section className={`section-panel ip-panel ${license.isPublic ? "ip-panel--public" : ""}`}>
            <div className="section-heading section-heading--capacity">
              <div><h2>{license.isPublic ? "Observed IPs" : "IP slots"}</h2><p>{license.isPublic ? "Hosting networks observed on this public-use license. Capacity enforcement is disabled." : "Hosting networks currently registered to this license."}</p></div>
              {license.isPublic
                ? <div className="unlimited-summary"><Globe2 size={17} /><span><strong>{license.usedIpAddresses} observed</strong><small>Unlimited capacity</small></span></div>
                : <div className="capacity-summary"><strong>{license.usedIpAddresses} / {license.maxIpAddresses} used</strong><Capacity total={license.maxIpAddresses} used={license.usedIpAddresses} /></div>}
            </div>
            {license.ipAddresses.length === 0 ? (
              <div className="inline-empty"><Server size={20} /><span><strong>No registered IPs</strong><small>{license.isPublic ? "The first successful plugin verification will appear here." : "The first successful plugin verification will claim a slot."}</small></span></div>
            ) : (
              <div className="ip-table">
                <div className="ip-table__head"><span>Address</span><span>First seen</span><span>Last seen</span><span>Eligibility</span><span /></div>
                {license.ipAddresses.map((ip, index) => {
                  const eligible = ip.removable && removeAvailable;
                  return (
                    <div className="ip-row" key={ip.id}>
                      <div className="ip-address"><span>{index + 1}</span><code>{ip.ipLabel}</code><CopyButton label="Copy masked IP" value={ip.ipLabel} /></div>
                      <div data-label="First seen"><strong>{formatDate(ip.firstSeenAt)}</strong><small>{relativeTime(ip.firstSeenAt)}</small></div>
                      <div data-label="Last seen"><strong>{formatDate(ip.lastSeenAt)}</strong><small>{relativeTime(ip.lastSeenAt)}</small></div>
                      <div data-label="Eligibility" className={eligible ? "eligibility eligibility--ready" : "eligibility"}>
                        {eligible ? <CheckCircle2 size={16} /> : <Clock3 size={16} />}
                        <span><strong>{eligible ? "Eligible" : "Cooling down"}</strong><small>{eligible ? "Available now" : !removeAvailable && license.removeAvailableAt ? relativeTime(license.removeAvailableAt) : relativeTime(ip.removableAt)}</small></span>
                      </div>
                      <Button disabled={!eligible && !account?.isAdmin} onClick={() => setPendingAction({ kind: "remove", ip })} variant="danger"><Trash2 size={15} /> Remove</Button>
                    </div>
                  );
                })}
              </div>
            )}
            <div className="ip-explainer"><TriangleAlert size={18} /><p><strong>Removal takes effect on the next license verification.</strong> {license.isPublic ? "Because this license is public, a removed server can register again when it next verifies." : "A running plugin may continue until it verifies again or restarts. If a different IP fills the freed slot first, this removed IP will be rejected on its next check."}</p></div>
          </section>

          <div className="split-panels">
            <section className="section-panel compact-panel">
              <div className="compact-panel__head"><Activity size={19} /><div><h2>{license.isPublic ? "Observed installations" : "Activation slots"}</h2><p>{license.isPublic ? "Tracked plugin instances with no enforced capacity" : "Persistent plugin installations"}</p></div></div>
              {license.isPublic ? <div className="unlimited-inline"><strong>{license.usedActivations} observed</strong><span>Unlimited</span></div> : <Capacity total={license.maxActivations} used={license.usedActivations} />}
              <div className="activation-list">
                {license.activations.length === 0 ? <span>No activations yet.</span> : license.activations.map((activation) => (
                  <div key={activation.id}><Fingerprint size={16} /><span><strong>{activation.label || "Minecraft server"}</strong><small>Last verified {relativeTime(activation.lastSeenAt)}</small></span></div>
                ))}
              </div>
            </section>
            <section className="section-panel compact-panel">
              <div className="compact-panel__head"><ShieldCheck size={19} /><div><h2>Offline protection</h2><p>Signed grace grants</p></div></div>
              <div className="health-state"><CheckCircle2 size={22} /><span><strong>Protected</strong><small>Temporary API outages use the last valid signed grant. Explicit API rejection is never bypassed.</small></span></div>
            </section>
          </div>
        </div>

        <aside className="control-rail">
          <section className="rail-section rail-section--danger">
            <div className="rail-section__icon"><RotateCcw size={19} /></div>
            <h2>{license.isPublic ? "Clear observed servers" : "Reset all IP slots"}</h2>
            <p>{license.isPublic ? "Remove every observed IP and installation. Active servers will appear again when they next verify." : "Remove every registered IP and plugin activation, freeing all license capacity."}</p>
            <Button disabled={!resetAvailable && !account?.isAdmin} onClick={() => setPendingAction({ kind: "reset" })} variant="danger"><RotateCcw size={16} /> Reset all IPs</Button>
            <span className="cooldown-copy"><Clock3 size={14} /> {resetAvailable ? "Available now" : `Available ${relativeTime(license.resetAvailableAt!)}`}</span>
          </section>
          <section className="rail-section">
            <div className="rail-section__icon"><Link2 size={19} /></div>
            <h2>Web access</h2>
            {license.webAccess ? (
              <div className="access-record">
                <span className="avatar-fallback">{(license.webAccess.account.displayName || license.webAccess.account.email || "W").slice(0, 1).toUpperCase()}</span>
                <span><strong>{license.webAccess.account.displayName || "Web account"}</strong><small>{license.webAccess.account.email || "Discord-connected account"}</small></span>
              </div>
            ) : <p>No normal web account manages this license.</p>}
            <span className="identity-state"><ShieldCheck size={15} /> Discord owner remains canonical</span>
          </section>
          <section className="rail-section">
            <div className="rail-section__icon"><CalendarClock size={19} /></div>
            <h2>Cooldown policy</h2>
            <dl className="policy-list">
              <div><dt>Single removal</dt><dd>24 hours</dd></div>
              <div><dt>Full reset</dt><dd>4 days</dd></div>
              <div><dt>Timers</dt><dd>Independent</dd></div>
            </dl>
          </section>
          {account?.isAdmin && license.discordSync && (
            <section className="rail-section">
              <div className="rail-section__icon"><Activity size={19} /></div>
              <h2>Discord synchronization</h2>
              {license.discordSync.operations.length === 0 ? <p>No queued Discord operations.</p> : (
                <dl className="policy-list">
                  {license.discordSync.operations.map((operation) => (
                    <div key={`${operation.type}-${operation.updatedAt}`}>
                      <dt>{operation.type}</dt>
                      <dd><StatusBadge status={operation.status} /></dd>
                    </div>
                  ))}
                </dl>
              )}
              {license.discordSync.lastError && <small className="cooldown-copy">Last error: {license.discordSync.lastError}</small>}
            </section>
          )}
          {account?.isAdmin && (
            <AdminLicenseControls license={license} onChanged={load} />
          )}
        </aside>
      </div>

      <ConfirmDialog
        busy={busy}
        confirmLabel={pendingAction?.kind === "reset" ? "Reset all IPs" : "Remove IP"}
        description={pendingAction ? (
          <div className="confirm-copy">
            <p>{pendingAction.kind === "reset"
              ? "Every registered IP and plugin activation will be deleted. The four-day reset cooldown starts immediately, and running plugins are affected when they verify again or restart."
              : `${pendingAction.ip.ipLabel} and its linked plugin activations will be removed. The 24-hour single-removal cooldown starts immediately, and that server must claim fresh slots when it verifies again.`}</p>
            {account?.isAdmin && (
              <div className="override-controls">
                <label className="check-field"><input checked={override} onChange={(event) => setOverride(event.target.checked)} type="checkbox" /> Override cooldown as administrator</label>
                {override && <label>Audit reason<input onChange={(event) => setOverrideReason(event.target.value)} placeholder="Customer support request" value={overrideReason} /></label>}
              </div>
            )}
          </div>
        ) : null}
        onCancel={() => setPendingAction(null)}
        onConfirm={() => void confirmAction()}
        open={Boolean(pendingAction)}
        title={pendingAction?.kind === "reset" ? "Reset all IP and activation slots?" : "Remove this IP and its activations?"}
      />
    </div>
  );
}

function ReleaseDownloads({ license }: { license: LicenseDetail }) {
  const [showHistory, setShowHistory] = useState(false);
  const published = license.availableReleases
    .filter((release) => release.status === "PUBLISHED")
    .sort((left, right) => {
      const leftDate = new Date(left.publishedAt || left.createdAt).getTime();
      const rightDate = new Date(right.publishedAt || right.createdAt).getTime();
      return rightDate - leftDate;
    });
  const visibleReleases = showHistory ? published : published.slice(0, 1);
  const previousVersionCount = Math.max(0, published.length - 1);

  return (
    <section className="section-panel release-downloads">
      <div className="section-heading">
        <div>
          <h2>{showHistory ? "Version history" : "Plugin downloads"}</h2>
          <p>{showHistory ? "All published builds, ordered from newest to oldest." : "The latest verified build for this product."}</p>
        </div>
        {previousVersionCount > 0 ? (
          <Button
            aria-controls="release-version-list"
            aria-expanded={showHistory}
            className="release-history-toggle"
            onClick={() => setShowHistory((current) => !current)}
            variant="ghost"
          >
            {showHistory ? <ArrowLeft size={16} /> : <History size={16} />}
            {showHistory ? "Back to latest" : `Version history (${previousVersionCount})`}
          </Button>
        ) : (
          <span className="release-count"><PackageCheck size={16} /> Latest release</span>
        )}
      </div>
      {published.length === 0 ? (
        <div className="inline-empty"><Download size={20} /><span><strong>No published builds</strong><small>The studio has not published a downloadable JAR yet.</small></span></div>
      ) : (
        <div className="release-list" id="release-version-list">
          {visibleReleases.map((release, index) => (
            <article className="release-card" key={release.id}>
              <div className="release-card__mark"><PackageCheck size={20} /></div>
              <div className="release-card__copy">
                <div>
                  <strong>Version {release.version}</strong>
                  <StatusBadge status={release.channel} />
                  {showHistory && index === 0 && <span className="release-latest-label">Latest</span>}
                </div>
                <p>{release.changelog || "Production-ready build published by oCloud Studio."}</p>
                <small>
                  {release.minecraftVersions ? `Minecraft ${release.minecraftVersions}` : "Minecraft compatibility listed by the product"}
                  {release.fileSize ? ` · ${formatBytes(release.fileSize)}` : ""}
                  {release.publishedAt ? ` · Published ${relativeTime(release.publishedAt)}` : ""}
                </small>
              </div>
              <a
                className="button button--primary"
                href={`/v1/web/licenses/${license.id}/releases/${release.id}/download`}
              >
                <Download size={16} /> Download JAR
              </a>
            </article>
          ))}
        </div>
      )}
      <div className="download-policy"><ShieldCheck size={16} /><span>Every download is entitlement-checked and recorded in the security audit trail.</span></div>
    </section>
  );
}

function AdminLicenseControls({ license, onChanged }: { license: LicenseDetail; onChanged(): Promise<unknown> }) {
  const [maxActivations, setMaxActivations] = useState(license.maxActivations);
  const [maxIpAddresses, setMaxIpAddresses] = useState(license.maxIpAddresses);
  const [expiresAt, setExpiresAt] = useState(toLocalDateTime(license.expiresAt));
  const [lifetime, setLifetime] = useState(!license.expiresAt);
  const [internalNote, setInternalNote] = useState(license.internalNote || "");
  const [notifyBuyer, setNotifyBuyer] = useState(true);
  const [reason, setReason] = useState("");
  const [newDiscordUserId, setNewDiscordUserId] = useState("");
  const [action, setAction] = useState<AdminAction | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [rotatedKey, setRotatedKey] = useState<string | null>(null);

  async function save(event: FormEvent) {
    event.preventDefault();
    if (!reason.trim()) return setError("An audit reason is required.");
    setBusy(true); setError(null); setNotice(null);
    try {
      await api(`/v1/web/admin/licenses/${license.id}`, {
        method: "PATCH",
        body: jsonBody({
          maxActivations,
          maxIpAddresses,
          expiresAt: lifetime ? null : new Date(expiresAt).toISOString(),
          internalNote,
          notifyBuyer,
          reason: reason.trim(),
        }),
      });
      setReason("");
      setNotice("License settings saved and synchronization was queued.");
      await onChanged();
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "License settings could not be saved.");
    } finally { setBusy(false); }
  }

  async function runAction() {
    if (!action || !reason.trim()) return;
    setBusy(true); setError(null); setNotice(null);
    try {
      const payload: Record<string, unknown> = { reason: reason.trim() };
      if (action === "transfer-owner") payload.newDiscordUserId = newDiscordUserId;
      if (action === "reactivate") payload.expiresAt = lifetime ? null : new Date(expiresAt).toISOString();
      if (action === "reset-activations") payload.notifyBuyer = notifyBuyer;
      if (action === "enable-public" || action === "disable-public") {
        payload.isPublic = action === "enable-public";
      }
      const endpoint = action === "enable-public" || action === "disable-public"
        ? "public-mode"
        : action;
      const result = await api<{ licenseKey?: string }>(`/v1/web/admin/licenses/${license.id}/${endpoint}`, {
        method: "POST",
        body: jsonBody(payload),
      });
      if (result?.licenseKey) setRotatedKey(result.licenseKey);
      setNotice(adminActionCopy(action));
      setReason(""); setNewDiscordUserId(""); setAction(null);
      await onChanged();
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "The administrative action failed.");
      setAction(null);
    } finally { setBusy(false); }
  }

  return (
    <section className="rail-section admin-license-controls">
      <div className="rail-section__icon"><UserRoundCog size={19} /></div>
      <h2>Administrator controls</h2>
      <p>Lifecycle, capacity, ownership, and Discord synchronization.</p>
      <div className={`public-mode-control ${license.isPublic ? "public-mode-control--active" : ""}`}>
        <span className="public-mode-control__icon"><Globe2 size={18} /></span>
        <div>
          <span className="public-mode-control__heading"><strong>Public-use license</strong><small>{license.isPublic ? "PUBLIC" : "PRIVATE"}</small></span>
          <p>{license.isPublic
            ? "Any server with the key can verify. IPs and installations remain observed, but capacity is not enforced."
            : "Verification currently enforces the configured activation and IP limits."}</p>
        </div>
        <Button onClick={() => setAction(license.isPublic ? "disable-public" : "enable-public")} variant={license.isPublic ? "secondary" : "primary"}>
          {license.isPublic ? "Return to private" : "Make public"}
        </Button>
      </div>
      <form className="admin-license-form" onSubmit={(event) => void save(event)}>
        <div className="admin-limit-grid">
          <label>Activation limit<input min={license.isPublic ? 1 : license.usedActivations} onChange={(event) => setMaxActivations(Number(event.target.value))} type="number" value={maxActivations} /></label>
          <label>IP limit<input min={license.isPublic ? 1 : license.usedIpAddresses} onChange={(event) => setMaxIpAddresses(Number(event.target.value))} type="number" value={maxIpAddresses} /></label>
        </div>
        {license.isPublic && <small className="admin-form-help">These limits are stored for a future return to private mode; they are not enforced while public use is enabled.</small>}
        <label className="check-field"><input checked={lifetime} onChange={(event) => setLifetime(event.target.checked)} type="checkbox" /> Lifetime license</label>
        {!lifetime && <label>Expiry date<input onChange={(event) => setExpiresAt(event.target.value)} required type="datetime-local" value={expiresAt} /></label>}
        <label>Internal note<textarea maxLength={1000} onChange={(event) => setInternalNote(event.target.value)} placeholder="Visible only to administrators" rows={3} value={internalNote} /></label>
        <label>Audit reason<input maxLength={500} onChange={(event) => setReason(event.target.value)} placeholder="Required for every change" value={reason} /></label>
        <label className="check-field"><input checked={notifyBuyer} onChange={(event) => setNotifyBuyer(event.target.checked)} type="checkbox" /> Notify the Discord owner</label>
        <Button disabled={busy || !reason.trim()} type="submit"><RefreshCw size={15} /> Save settings</Button>
      </form>
      <div className="admin-action-grid">
        {license.status === "ACTIVE" && <Button onClick={() => setAction("suspend")} variant="danger"><PauseCircle size={15} /> Suspend</Button>}
        {license.status === "SUSPENDED" && <Button onClick={() => setAction("resume")} variant="secondary"><PlayCircle size={15} /> Resume</Button>}
        {license.status === "EXPIRED" && <Button onClick={() => setAction("reactivate")} variant="secondary"><PlayCircle size={15} /> Reactivate</Button>}
        <Button onClick={() => setAction("rotate")} variant="secondary"><KeyRound size={15} /> Rotate key</Button>
        <Button onClick={() => setAction("resend-key")} variant="secondary"><Download size={15} /> Resend key</Button>
        <Button onClick={() => setAction("sync-role")} variant="secondary"><ShieldCheck size={15} /> Sync role</Button>
        <Button onClick={() => setAction("reset-activations")} variant="secondary"><RotateCcw size={15} /> Reset activations</Button>
        <Button onClick={() => setAction("transfer-owner")} variant="secondary"><UserRoundCog size={15} /> Transfer owner</Button>
      </div>
      {rotatedKey && <div className="rotated-key"><span>New license key</span><code>{rotatedKey}</code><CopyButton label="Copy new license key" value={rotatedKey} /></div>}
      {error && <InlineNotice kind="danger">{error}</InlineNotice>}
      {notice && <InlineNotice kind="success">{notice}</InlineNotice>}
      <ConfirmDialog
        busy={busy}
        confirmDisabled={!reason.trim() || (action === "transfer-owner" && !/^\d{17,20}$/.test(newDiscordUserId))}
        confirmLabel={action ? adminActionLabel(action) : "Confirm"}
        description={action ? <div className="form-stack">
          <p>{adminActionDescription(action)}</p>
          {action === "transfer-owner" && <label>New Discord user ID<input inputMode="numeric" onChange={(event) => setNewDiscordUserId(event.target.value.replace(/\D/g, ""))} placeholder="17–20 digit Discord ID" value={newDiscordUserId} /></label>}
          <label>Audit reason<input maxLength={500} onChange={(event) => setReason(event.target.value)} placeholder="Why is this action necessary?" value={reason} /></label>
        </div> : null}
        onCancel={() => setAction(null)}
        onConfirm={() => void runAction()}
        open={Boolean(action)}
        title={action ? adminActionLabel(action) : "Administrative action"}
        tone={action === "suspend" || action === "enable-public" ? "danger" : "primary"}
      />
    </section>
  );
}

function adminActionLabel(action: AdminAction) {
  return ({
    "disable-public": "Return license to private use",
    "enable-public": "Make license public",
    reactivate: "Reactivate license",
    "resend-key": "Resend key",
    "reset-activations": "Reset activations",
    resume: "Resume license",
    rotate: "Rotate license key",
    suspend: "Suspend license",
    "sync-role": "Synchronize role",
    "transfer-owner": "Transfer ownership",
  })[action];
}

function adminActionDescription(action: AdminAction) {
  return ({
    "disable-public": "Resume activation and IP capacity enforcement. This action is blocked until observed usage fits within the stored private limits.",
    "enable-public": "Allow any server with this key to verify without activation or IP limits. Every new IP remains recorded and reported, while dashboard ownership stays restricted.",
    reactivate: "Restore this expired license with the expiry settings currently shown in the editor.",
    "resend-key": "Send the current recoverable license key to the canonical Discord owner again.",
    "reset-activations": "Delete every registered plugin instance while preserving its IP records.",
    resume: "Restore verification and the mapped Discord product role.",
    rotate: "Invalidate the old key immediately, create a new key, and deliver it to the owner.",
    suspend: "Temporarily reject verification and remove the mapped Discord product role.",
    "sync-role": "Re-apply the product role to the current Discord owner.",
    "transfer-owner": "Move canonical ownership, dashboard discovery, role, and key delivery to another Discord user.",
  })[action];
}

function adminActionCopy(action: AdminAction) {
  return `${adminActionLabel(action)} completed or queued for Discord synchronization.`;
}

function toLocalDateTime(value: string | null) {
  const date = value ? new Date(value) : new Date(Date.now() + 30 * 24 * 60 * 60 * 1000);
  const local = new Date(date.getTime() - date.getTimezoneOffset() * 60_000);
  return local.toISOString().slice(0, 16);
}

function formatBytes(value: number) {
  if (value < 1024 * 1024) return `${Math.max(1, Math.round(value / 1024))} KB`;
  return `${(value / (1024 * 1024)).toFixed(1)} MB`;
}
